import React, { useCallback, useEffect, useState } from 'react';
import { Check, X, Pin, PinOff, Pencil, Trash2, Plus, Sparkles, Spline } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { twineAPI, TWINE_KINDS, SOURCE_LABEL, twineChanged } from '@/lib/twineAPI';
import { safeFormat } from '@/lib/utils';

const KIND_STYLE = {
  fact: 'bg-[#F3EFE7] text-[#1E2A33] border-[#E3DDD1]',
  preference: 'bg-sky-50 text-sky-800 border-sky-200',
  decision: 'bg-emerald-50 text-emerald-800 border-emerald-200',
  date: 'bg-amber-50 text-amber-800 border-amber-200',
  person: 'bg-violet-50 text-violet-800 border-violet-200',
  caution: 'bg-rose-50 text-rose-800 border-rose-200',
};

export const KindBadge = ({ kind }) => (
  <span className={`inline-block rounded-full border px-2 py-0.5 text-[11px] font-medium shrink-0 ${KIND_STYLE[kind] || KIND_STYLE.fact}`}>
    {(TWINE_KINDS.find((k) => k.id === kind) || TWINE_KINDS[0]).label}
  </span>
);

export const SourceLine = ({ s }) => (
  <div className="text-xs text-[#5C6670] mt-0.5">
    {s.source?.label || SOURCE_LABEL[s.source?.type] || 'Added'}
    {s.source?.excerpt ? <span className="italic"> · “{s.source.excerpt}”</span> : null}
    {s.kept_by?.name ? ` · kept by ${s.kept_by.name}` : ''}
    {s.kept_at || s.created_at ? ` · ${safeFormat(s.kept_at || s.created_at, 'MMM d, yyyy')}` : ''}
  </div>
);

// One strand: read, keep/dismiss (suggestions), edit, pin, delete
export const StrandRow = ({ s, onChanged, showSubject = false }) => {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(s.text);
  const [kind, setKind] = useState(s.kind);
  const act = async (data, msg) => {
    try { await twineAPI.update(s.id, data); if (msg) toast.success(msg); twineChanged(); onChanged(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Could not update'); }
  };
  const save = async () => { await act({ text, kind }); setEditing(false); };
  const suggested = s.status === 'suggested';
  return (
    <li className={`py-2.5 ${suggested ? 'bg-[#FBF8F2] -mx-3 px-3 rounded-lg' : ''}`}>
      {editing ? (
        <div className="space-y-2">
          <Input value={text} onChange={(e) => setText(e.target.value)} maxLength={280} autoFocus />
          <div className="flex gap-2 items-center flex-wrap">
            <select value={kind} onChange={(e) => setKind(e.target.value)} className="h-8 rounded-md border border-[#E3DDD1] bg-white px-2 text-sm">
              {TWINE_KINDS.map((k) => <option key={k.id} value={k.id}>{k.label}</option>)}
            </select>
            <Button size="sm" className="bg-[#A62B1F] hover:bg-[#8C2318] text-white" onClick={save}>Save</Button>
            <Button size="sm" variant="ghost" onClick={() => { setEditing(false); setText(s.text); setKind(s.kind); }}>Cancel</Button>
          </div>
        </div>
      ) : (
        <div className="flex items-start gap-2">
          <div className="min-w-0 flex-1">
            <div className="flex items-start gap-2 flex-wrap">
              {s.pinned && <Pin className="w-3.5 h-3.5 text-[#A62B1F] mt-1 shrink-0" />}
              <span className="text-sm text-[#1E2A33]">{s.text}</span>
              <KindBadge kind={s.kind} />
            </div>
            {showSubject && s.subject?.label && <div className="text-xs font-semibold text-[#1E2A33] mt-0.5">On {s.subject.label}</div>}
            {s.replaces_text && <div className="text-xs text-[#5C6670] mt-0.5">Replaces: <span className="line-through">{s.replaces_text}</span></div>}
            <SourceLine s={s} />
          </div>
          <div className="flex gap-0.5 shrink-0">
            {suggested ? (
              <>
                <Button size="sm" variant="ghost" title="Keep" className="text-emerald-700" onClick={() => act({ status: 'kept' }, 'Kept')}><Check className="w-4 h-4" /></Button>
                <Button size="sm" variant="ghost" title="Edit, then keep" onClick={() => setEditing(true)}><Pencil className="w-4 h-4" /></Button>
                <Button size="sm" variant="ghost" title="Dismiss" className="text-[#5C6670]" onClick={() => act({ status: 'dismissed' })}><X className="w-4 h-4" /></Button>
              </>
            ) : (
              <>
                <Button size="sm" variant="ghost" title={s.pinned ? 'Unpin' : 'Pin to the top'} onClick={() => act({ pinned: !s.pinned })}>{s.pinned ? <PinOff className="w-4 h-4" /> : <Pin className="w-4 h-4" />}</Button>
                <Button size="sm" variant="ghost" title="Edit" onClick={() => setEditing(true)}><Pencil className="w-4 h-4" /></Button>
                <Button size="sm" variant="ghost" title="Delete" className="text-[#A62B1F]" onClick={async () => {
                  if (!window.confirm('Remove this from the Twine?')) return;
                  try { await twineAPI.remove(s.id); onChanged(); } catch (e) { toast.error(e?.response?.data?.detail || 'Could not delete'); }
                }}><Trash2 className="w-4 h-4" /></Button>
              </>
            )}
          </div>
        </div>
      )}
    </li>
  );
};

/**
 * The Twine on a record. subject = { type: 'contact'|'account'|'project'|'interview', id, label }
 * mendFrom (optional) = { type: 'interview'|'yarn'|'activity', id, label } adds a "Remember from this" button.
 */
export default function TwinePanel({ subject, mendFrom = null, className = '' }) {
  const [rows, setRows] = useState(null);
  const [adding, setAdding] = useState(false);
  const [text, setText] = useState('');
  const [kind, setKind] = useState('fact');
  const [mending, setMending] = useState(false);

  const load = useCallback(() => {
    if (!subject?.id) return;
    twineAPI.list(subject).then((r) => setRows(r.data)).catch(() => setRows([]));
  }, [subject?.type, subject?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    try { await twineAPI.add(subject, text, kind); setText(''); setKind('fact'); setAdding(false); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Could not add'); }
  };
  const mend = async () => {
    setMending(true);
    try {
      const r = await twineAPI.mend(mendFrom.type, mendFrom.id);
      const n = r.data.suggested.length;
      toast.success(n ? `${n} suggestion${n > 1 ? 's' : ''} to review` : 'Nothing new to remember');
      twineChanged(); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not read it'); }
    finally { setMending(false); }
  };

  const kept = (rows || []).filter((s) => s.status === 'kept');
  const suggested = (rows || []).filter((s) => s.status === 'suggested');

  return (
    <div className={`rounded-xl border border-[#E3DDD1] bg-white p-4 md:p-5 ${className}`}>
      <div className="flex items-center justify-between gap-2 mb-1 flex-wrap">
        <h3 className="font-semibold text-[#1E2A33] flex items-center gap-2"><Spline className="w-4 h-4 text-[#A62B1F]" />The Twine</h3>
        <div className="flex gap-2">
          {mendFrom && (
            <Button size="sm" variant="outline" onClick={mend} disabled={mending} title="Read it and suggest what's worth remembering">
              <Sparkles className="w-4 h-4 mr-1" />{mending ? 'Reading…' : 'Remember from this'}
            </Button>
          )}
          <Button size="sm" variant="outline" onClick={() => setAdding((a) => !a)}><Plus className="w-4 h-4 mr-1" />Add</Button>
        </div>
      </div>
      <p className="text-xs text-[#5C6670] mb-3">What the team knows about {subject?.label || 'this'}. Suggestions are only kept once someone checks them.</p>

      {adding && (
        <div className="space-y-2 mb-3">
          <Input value={text} onChange={(e) => setText(e.target.value)} maxLength={280} autoFocus
            placeholder="e.g. Prefers a text before a call" onKeyDown={(e) => { if (e.key === 'Enter' && text.trim()) add(); }} />
          <div className="flex gap-2 items-center">
            <select value={kind} onChange={(e) => setKind(e.target.value)} className="h-8 rounded-md border border-[#E3DDD1] bg-white px-2 text-sm">
              {TWINE_KINDS.map((k) => <option key={k.id} value={k.id}>{k.label}</option>)}
            </select>
            <Button size="sm" className="bg-[#A62B1F] hover:bg-[#8C2318] text-white" disabled={!text.trim()} onClick={add}>Keep it</Button>
            <span className="text-xs text-[#5C6670]">No health details, ID numbers or passwords.</span>
          </div>
        </div>
      )}

      {rows === null ? <p className="text-sm text-[#5C6670]">Loading…</p> : (
        <>
          {suggested.length > 0 && (
            <div className="mb-3">
              <div className="text-[11px] uppercase tracking-wider font-semibold text-[#A62B1F] mb-1">To review ({suggested.length})</div>
              <ul className="divide-y divide-[#E3DDD1]">{suggested.map((s) => <StrandRow key={s.id} s={s} onChanged={load} />)}</ul>
            </div>
          )}
          {kept.length === 0 && suggested.length === 0 ? (
            <p className="text-sm text-[#5C6670]">Nothing yet. Add what you know, or let a yarn or interview suggest it.</p>
          ) : kept.length > 0 && (
            <ul className="divide-y divide-[#E3DDD1]">{kept.map((s) => <StrandRow key={s.id} s={s} onChanged={load} />)}</ul>
          )}
        </>
      )}
    </div>
  );
}
